import {FormControl, FormGroup, Validators} from '@angular/forms';
import {AmountControl, BaseControl, DatepickerControl, Helper} from "../../../../shared/classes";
import {ComponentTypesEnum} from "../../../../shared/enums";
import {HttpStatusCodesEnum, FundOrderReportInterface, FundOrdersService} from "@spa/api";
import {FormComponent} from "../../../../shared/widgets";
import {AlertsService} from "appShared/services";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {
    FundProductTypesEnum
} from "appEntries/panel/pages/panel-accounts/pages/panel-accounts-edit/panel-accounts-edit.imports";
import * as moment from "moment/moment";

export const OrderAmountSelector = 'orderAmount';
export const OrderPriceSelector = 'orderPrice';
export const OrderQuantitySelector = 'orderQuantity';
export const OrderExecutionCreatedOnSelector = 'orderExecutionCreatedOn';
export const MinNumberValue = 0.0001;
export const DateAndTimeFormatExample = '2024-06-22T13:17:21';

export class OrderUpdateForm {
    public form: FormGroup;
    public control: OrderAmountControlInterface;
    public dateFormatExample = DateAndTimeFormatExample;

    constructor(
        private readonly ordersService: FundOrdersService,
        private readonly alertsService: AlertsService,
        private order: FundOrderReportInterface) {

    }

    private parseDateAndTimeString(value: string): (Date | null) {
        const parsed = moment(value, moment.ISO_8601, true);

        if (parsed.isValid()) {
            return parsed.toDate();
        }

        return null;
    }

    validateForm(): boolean {
        const amountControl = this.form.get(this.control.amount.name);
        const priceControl = this.form.get(this.control.price.name);
        const quantityControl = this.form.get(this.control.quantity.name);
        const executionDateControl = this.form.get(this.control.executionCreatedOn.name);

        const executionDate = this.parseDateAndTimeString(executionDateControl.value);

        let isValid = true;

        if (!executionDate) {
            this.control.executionCreatedOn.errors = [`Invalid date format. Please use as: "${DateAndTimeFormatExample}"`];

            isValid = false;
        }
        else if (executionDate > new Date()) {
            this.control.executionCreatedOn.errors = ['Please enter a date less than current date.'];

            isValid = false;
        }

        if (this.form.invalid) {
            if (amountControl && amountControl.invalid) {
                this.control.amount.errors = Object.keys(amountControl.errors).map(errorKey => {
                    return FormComponent.getFormMessages(this.control.amount.placeholder, MinNumberValue)[errorKey];
                });
            }

            if (priceControl && priceControl.invalid) {
                this.control.price.errors = Object.keys(priceControl.errors).map(errorKey => {
                    return FormComponent.getFormMessages(this.control.price.placeholder, MinNumberValue)[errorKey];
                });
            }

            if (quantityControl && quantityControl.invalid) {
                this.control.quantity.errors = Object.keys(quantityControl.errors).map(errorKey => {
                    return FormComponent.getFormMessages(this.control.quantity.placeholder, MinNumberValue)[errorKey];
                });
            }

            isValid = false;
        }

        return isValid;
    }

    initForm(): void {
        const isBonds = this.order.productType === FundProductTypesEnum.Bonds;
        const executionDate = this.order.executionCreatedOn
            ? moment(this.order.executionCreatedOn, moment.ISO_8601, true).toISOString(true)
            : '';

        this.form = new FormGroup({});
        this.control = {
            amount: new AmountControl({
                name: OrderAmountSelector,
                value: this.order.amount?.toString(),
                placeholder: 'Amount',
                validators: [Validators.required, Validators.min(MinNumberValue)],
                componentType: ComponentTypesEnum.AmountInput,
                currency: this.order.currency,
                fourDecimals: true
            }),
            price: new AmountControl({
                name: OrderPriceSelector,
                value: this.order.price?.toString(),
                placeholder: 'Price',
                validators: [Validators.required, Validators.min(MinNumberValue)],
                componentType: ComponentTypesEnum.AmountInput,
                currency: isBonds ? '' : this.order.currency,
                isPercentage: isBonds,
                fourDecimals: true
            }),
            quantity: new AmountControl({
                name: OrderQuantitySelector,
                value: this.order.quantity?.toString(),
                placeholder: 'Quantity',
                validators: [Validators.required, Validators.min(MinNumberValue)],
                componentType: ComponentTypesEnum.AmountInput,
                currency: '',
                isPercentage: false,
                fourDecimals: true
            }),
            executionCreatedOn: new BaseControl({
                name: OrderExecutionCreatedOnSelector,
                value: executionDate,
                placeholder: `Execution Date (${Helper.getTimeZone()})`,
                validators: [],
                componentType: ComponentTypesEnum.Text,

            })
        }

        this.form.addControl(this.control.amount.name, new FormControl(this.control.amount.value, this.control.amount.validators));
        this.form.addControl(this.control.price.name, new FormControl(this.control.price.value, this.control.price.validators));
        this.form.addControl(this.control.quantity.name, new FormControl(this.control.quantity.value, this.control.quantity.validators));
        this.form.addControl(this.control.executionCreatedOn.name, new FormControl(this.control.executionCreatedOn.value, this.control.executionCreatedOn.validators));
    }

    submitForm(): Observable<void> {
        const amount = Helper.getAmountValue(this.control.amount.value);
        const price = Helper.getAmountValue(this.control.price.value);
        const quantity = Helper.getAmountValue(this.control.quantity.value);
        const executionCreatedOnString = this.control.executionCreatedOn.value as string;
        const executionCreatedOn = this.parseDateAndTimeString(executionCreatedOnString);

        return this.ordersService.updateOrderAmount({
            orderId: this.order.orderId,
            amount: amount,
            price: price,
            quantity: quantity,
            executionCreatedOn: executionCreatedOn
        }).pipe(map(response => {
            if (response.status !== HttpStatusCodesEnum.Ok) {
                this.alertsService.addAlert(`Update order failed ${response.status}`);

                return;
            }

            const newOrder = response.data as FundOrderReportInterface;

            this.order.amount = newOrder.amount;
            this.order.quantity = newOrder.quantity;
            this.order.price = newOrder.price;
            this.order.executionCreatedOn = newOrder.executionCreatedOn;
        }));
    }
}

export interface OrderAmountControlInterface {
    amount: AmountControl;
    quantity: AmountControl;
    price: AmountControl;
    executionCreatedOn: BaseControl;
}
